import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/leadUtils";
import VoidExpenseModal from "@/components/accounts/VoidExpenseModal";

/**
 * Phase 7.0 — Project Expenses section restyle.
 * Lives inside the 440px-wide Accounts payment slideover. Expense rows are
 * floating pills (consistent with the rest of the Accounts pattern) but the
 * inline-add form uses a single-column field stack since the drawer is narrow.
 *
 * VISUAL-ONLY: every data-testid, every state hook, every axios call
 * (`/projects/{id}/expenses`, `/catalogs`, `/expenses`), every toast,
 * `VoidExpenseModal` integration, and `onChanged()` upstream notifier are
 * preserved bit-for-bit.
 */

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ProjectExpensesSection({ projectId, onChanged }) {
  const [expenses, setExpenses] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ category: "", amount: "", description: "", date: todayIso() });
  const [submitting, setSubmitting] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [error, setError] = useState("");

  const refresh = async () => {
    const { data } = await api.get(`/projects/${projectId}/expenses`);
    setExpenses(data.items || []);
    setSubtotal(data.subtotal || 0);
  };

  const loadCategories = async () => {
    const { data } = await api.get("/catalogs");
    const cats = (data.expense_categories || [])
      .filter((c) => c.active)
      .sort((a, b) => {
        const aRank = a.default_type === "project_direct" ? 0 : 1;
        const bRank = b.default_type === "project_direct" ? 0 : 1;
        if (aRank !== bRank) return aRank - bRank;
        return a.name.localeCompare(b.name);
      });
    setCategories(cats);
    if (!form.category && cats.length) setForm((f) => ({ ...f, category: cats[0].name }));
  };

  useEffect(() => {
    refresh();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const submit = async () => {
    setError("");
    if (!form.category) { setError("Pick a category."); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { setError("Amount must be greater than 0."); return; }
    if (!form.date) { setError("Date is required."); return; }
    setSubmitting(true);
    try {
      await api.post("/expenses", {
        type: "project_direct",
        category: form.category,
        amount: amt,
        description: form.description || null,
        date: form.date,
        project_id: projectId,
      });
      toast.success(`Expense of ${fmtINR(amt)} added`);
      setForm({ category: categories[0]?.name || "", amount: "", description: "", date: todayIso() });
      setAdding(false);
      refresh();
      onChanged && onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not add expense.");
    } finally {
      setSubmitting(false);
    }
  };

  const onVoided = () => { setVoidTarget(null); refresh(); onChanged && onChanged(); };

  return (
    <div data-testid="project-expenses-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="project-expenses-toggle"
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          width: "100%", marginBottom: 8, padding: 0,
          background: "transparent", border: "none", cursor: "pointer",
        }}
      >
        <span
          style={{
            fontSize: 10.5, fontWeight: 600, letterSpacing: "0.16em",
            textTransform: "uppercase", color: "#A39A8B",
          }}
        >
          Project expenses · <span style={{ color: "#A35C45", fontWeight: 700 }}>{fmtINR(subtotal)}</span>
        </span>
        {open
          ? <ChevronDown size={14} color="#A39A8B" />
          : <ChevronRight size={14} color="#A39A8B" />}
      </button>

      {open && (
        <div>
          {expenses.length === 0 ? (
            <p
              data-testid="project-expenses-empty"
              style={{
                fontSize: 12, fontStyle: "italic", color: "#A39A8B",
                padding: "10px 0",
              }}
            >
              No project expenses recorded yet.
            </p>
          ) : (
            <div
              data-testid="project-expenses-list"
              style={{ display: "flex", flexDirection: "column", gap: 4 }}
            >
              {expenses.map((e) => (
                <div
                  key={e.id}
                  data-testid={`project-expense-${e.id}`}
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #EDE5D8",
                    borderRadius: 8,
                    padding: "8px 12px",
                    boxShadow: "0 2px 6px rgba(60,46,20,.04)",
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#22382C", lineHeight: 1.3 }}>
                      {e.category}{" "}
                      <span style={{ fontSize: 11, color: "#A39A8B", fontWeight: 400 }}>
                        · {fmtDate(e.date)}
                      </span>
                    </div>
                    {e.description && (
                      <div style={{ fontSize: 11, color: "#76705F", marginTop: 2 }}>
                        {e.description}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#A35C45" }}>
                      − {fmtINR(e.amount)}
                    </div>
                    <button
                      type="button"
                      onClick={() => setVoidTarget(e)}
                      data-testid={`project-expense-void-${e.id}`}
                      style={{
                        fontSize: 10, color: "#A39A8B", marginTop: 3,
                        background: "transparent", border: "none", cursor: "pointer",
                        textDecoration: "underline", padding: "2px 0", minHeight: 22,
                      }}
                    >
                      Void
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {adding ? (
            <div
              data-testid="project-expense-form"
              style={{
                background: "#FFFFFF",
                border: "1px solid #E9E2D6",
                borderRadius: 8,
                padding: 12,
                marginTop: 10,
                display: "flex", flexDirection: "column", gap: 10,
                boxShadow: "0 2px 6px rgba(60,46,20,.04)",
              }}
            >
              <PesField label="Category">
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="pes-fld"
                  data-testid="project-expense-category"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </PesField>
              <PesField label="Amount">
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="pes-fld"
                  data-testid="project-expense-amount"
                  placeholder="0"
                />
              </PesField>
              <PesField label="Date">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="pes-fld"
                  data-testid="project-expense-date"
                />
              </PesField>
              <PesField label="Description (optional)">
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="pes-fld"
                  data-testid="project-expense-description"
                />
              </PesField>
              {error && (
                <p
                  data-testid="project-expense-error"
                  style={{ fontSize: 11, color: "#A35C45", margin: 0 }}
                >
                  {error}
                </p>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 2 }}>
                <button
                  type="button"
                  onClick={() => { setAdding(false); setError(""); }}
                  disabled={submitting}
                  data-testid="project-expense-cancel"
                  className="pes-ghost-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="project-expense-save"
                  className="pes-primary-btn"
                >
                  {submitting ? "Saving…" : "Save expense"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid="project-expense-add-button"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                marginTop: 10,
                padding: "6px 12px", minHeight: 28,
                fontSize: 10.5, fontWeight: 600, letterSpacing: "0.13em",
                textTransform: "uppercase",
                background: "#FFFFFF", color: "#22382C",
                border: "1px solid #E4DCCD", borderRadius: 6,
                cursor: "pointer", transition: "border-color .15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B08D55")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E4DCCD")}
            >
              <Plus size={12} /> Add expense
            </button>
          )}

          {expenses.length > 0 && (
            <div
              style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginTop: 10, paddingTop: 10,
                borderTop: "1px solid #EDE5D8",
              }}
            >
              <span
                style={{
                  fontSize: 10.5, fontWeight: 700, letterSpacing: "0.16em",
                  textTransform: "uppercase", color: "#22382C",
                }}
              >
                Subtotal
              </span>
              <span
                data-testid="project-expenses-subtotal"
                style={{ fontSize: 14, fontWeight: 600, color: "#A35C45" }}
              >
                − {fmtINR(subtotal)}
              </span>
            </div>
          )}
        </div>
      )}

      {voidTarget && (
        <VoidExpenseModal
          expense={voidTarget}
          onClose={() => setVoidTarget(null)}
          onVoided={onVoided}
        />
      )}

      <style>{`
        .pes-fld{
          width:100%; padding:8px 10px;
          background:#FCFAF5; border:1px solid #E4DCCD; border-radius:7px;
          font-family:var(--ds-body-font);
          font-size:13px; color:#2A2722; outline:none;
          transition:border-color .15s, box-shadow .15s;
        }
        .pes-fld:focus{
          border-color:#B08D55;
          box-shadow:0 0 0 3px rgba(176,141,85,.14);
        }
        .pes-primary-btn{
          padding:7px 14px; font-size:10.5px; font-weight:600;
          letter-spacing:0.13em; text-transform:uppercase;
          background:#22382C; color:#F4EFE4;
          border:1px solid #22382C; border-radius:6px;
          cursor:pointer; transition:background .15s;
        }
        .pes-primary-btn:hover:not(:disabled){background:#2C4A3B;}
        .pes-primary-btn:disabled{
          background:#EFE8DC; color:#B7AE9E; border-color:#EFE8DC; cursor:not-allowed;
        }
        .pes-ghost-btn{
          padding:7px 12px; font-size:10.5px; font-weight:600;
          letter-spacing:0.13em; text-transform:uppercase;
          background:transparent; color:#76705F;
          border:1px solid transparent; border-radius:6px;
          cursor:pointer; transition:border-color .15s, color .15s;
        }
        .pes-ghost-btn:hover:not(:disabled){border-color:#E4DCCD; color:#22382C;}
        .pes-ghost-btn:disabled{opacity:0.5; cursor:not-allowed;}
      `}</style>
    </div>
  );
}

function PesField({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 10, fontWeight: 600, letterSpacing: "0.16em",
          textTransform: "uppercase", color: "#A39A8B", marginBottom: 5,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}
